import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useUpdateContact } from "../../../hooks/data/data_contract";
import { IContract } from "../../../interfaces/IContract";
import { IContractPosition } from "../../../interfaces/IContractPosition";
import { IContractState } from "../../../interfaces/IContractState";
import { IContractType } from "../../../interfaces/IContractType";
import { IOfferPartner } from "../../../interfaces/IOfferPartner";
import { IOfferPosition } from "../../../interfaces/IOfferPosition";
import { IProject } from "../../../interfaces/IProject";
import { IService } from "../../../interfaces/IService";
import { IServiceCategory } from "../../../interfaces/IServiceCategory";
import { IUnit } from "../../../interfaces/IUnit";
import { useSnackbar } from "../../context/SnackbarContext";
import { CustomeTextField } from "../../core/CustomeTextField";
import { getTmpId } from "../../core/generic_helpders";
import { OfferPositionAdd } from "../../offer/Childs/OfferPositionAdd";
import { getEmptyOffer } from "../../offer/OfferTable";
import { EditorContractPosition } from "./EditorContractPosition";
import { IPartner } from "../../../interfaces/IPartner";
import { useGetPartners } from "../../../hooks/data/data_partner";
import { roundToFloat } from "../../../utlis/Math";
import { LoadingDialog } from "../../core/LoadingDialog";
import { LoadingSpinner } from "../../core/LoadingSpinner";

interface IProps {
    project: IProject;
    orgContract: IContract;
    editContract: IContract;
    setEditContract: Function;
    contractStateArray: IContractState[];
    contractTypeArray: IContractType[];
    serviceCategoryArray: IServiceCategory[];
    serviceArray: IService[];
    unitArray: IUnit[];
    handleClose: () => void;
    handleSave: () => void;
}

const calculateTotal = (positions: IContractPosition[], contractTypeId: number): number => {
    if (contractTypeId === 20) {
        // Werkvertrag: price ist schon der Teilbetrag
        return positions.reduce((sum, p) => sum + p.price, 0);
    }
    // andere Verträge: Preis pro Einheit × Einheiten
    return positions.reduce((sum, p) => sum + p.price * p.units, 0);
};

export const EditorContract: React.FC<IProps> = ({
    project,
    orgContract,
    editContract,
    setEditContract,
    contractStateArray,
    contractTypeArray,
    serviceCategoryArray,
    serviceArray,
    unitArray,
    handleClose,
    handleSave,
}) => {
    console.log(editContract);
    const { showFeedback } = useSnackbar();

    const { updateContract, isPendingUpdateContract } = useUpdateContact();
    const [contractPositions, setContractPositions] = useState(editContract.ContactPositions);

    const { partnerArray, isLoadingPartnerArray, refetchPartnerArray } = useGetPartners();

    const [isOpenOfferPost, setIsOpenOfferPost] = useState(false);

    const currentContractType = contractTypeArray.find((x) => x.id === editContract.ContractType);
    const [totalValue, setTotalValue] = useState(
        calculateTotal(contractPositions, contractTypeArray.find((x) => x.id === editContract.ContractType)?.id || 0),
    );
    const is_superuser = localStorage.getItem("is_superuser") === "true";
    const isDisabled = currentContractType === undefined || (currentContractType.isEditJustAdmin && !is_superuser);

    const calcPricePerHour = () => {
        if (contractPositions.length > 0) {
            return { ppp: contractPositions[0].price_per_hour };
        } else {
            return { ppp: 0 };
        }
    };

    const updatePositionPrices = (newTotal: number) =>
        setContractPositions((prev) =>
            prev.map((pos) => ({
                ...pos,
                price: roundToFloat(newTotal * pos.units, 2) || 0,
            })),
        );

    const handleTotalChange = (val: { price: number }) => {
        const newTotal = val.price;

        if (isNaN(newTotal) || newTotal <= 0) {
            showFeedback("Bitte einen gültigen Auftragswert eingeben!", "error");
            return;
        }

        setTotalValue(newTotal);

        // nur beim Werkvertrag die Positionen mitskalieren
        if (currentContractType?.id === 20) {
            updatePositionPrices(newTotal);
        }
    };

    const [pricePerHour, setPricePerHour] = useState(calcPricePerHour());

    useEffect(() => {
        if (currentContractType?.id !== 20) {
            setTotalValue(calculateTotal(contractPositions, currentContractType?.id || 0));
        }
    }, [contractPositions]);

    const castOfferPosToContractPos = (offerPositionArray: IOfferPosition[]) => {
        let tmpArray: IContractPosition[] = [];
        let tmpId = getTmpId(contractPositions);
        offerPositionArray.map((offerPos, idx) =>
            tmpArray.push({
                id: tmpId - idx,
                articelNumber: offerPos.articelNumber,
                Contract: editContract.id,
                description: offerPos.description,
                position: 0,
                price: roundToFloat(offerPos.price, 2) || 0,
                price_per_hour: offerPos.price_per_hour,
                Service: offerPos.Service,
                timeFactor: offerPos.timeFactor,
                title: offerPos.title,
                Unit: offerPos.Unit,
                units: offerPos.units,
            }),
        );
        setContractPositions((prev) => [...prev, ...tmpArray]);
        setIsOpenOfferPost(false);
    };

    const recalcContractPositionsPrice = (pObject: { ppp: number }) => {
        let ppp = pObject.ppp;
        if (ppp === undefined || ppp === null || isNaN(ppp)) {
            showFeedback("Bitte einen gültigen Preis pro Stunde eingeben!", "error");
            return;
        }
        if (ppp < 0) {
            showFeedback("Der Preis pro Stunde darf nicht negativ sein!", "error");
            return;
        }
        if (ppp === 0) {
            showFeedback("Der Preis pro Stunde darf nicht 0 sein!", "error");
            return;
        }
        let contractPositonsTemp: IContractPosition[] = [];
        contractPositions.forEach((pos) => {
            contractPositonsTemp.push({
                ...pos,
                price_per_hour: ppp,
                price: roundToFloat(pos.units * pos.timeFactor * ppp, 2) || 0,
            });
        });

        setContractPositions(contractPositonsTemp);
        setPricePerHour({ ppp });
    };

    const handleNew = () => {
        if (currentContractType?.id === 20) {
            setContractPositions((prev) => [
                ...prev,
                {
                    articelNumber: String(contractPositions.length + 1),
                    Contract: editContract.id,
                    description: "",
                    id: getTmpId(contractPositions),
                    position: contractPositions.length + 1,
                    price: 0,
                    price_per_hour: 0,
                    Service: null,
                    timeFactor: 0,
                    title: "",
                    Unit: 9,
                    units: 0,
                },
            ]);
        } else {
            setIsOpenOfferPost(true);
        }
    };

    const handleUpload = async () => {
        let uploadObject: IContract = {
            ...editContract,
            ContactPositions: contractPositions,
        };
        try {
            await updateContract(uploadObject);
            showFeedback("Erfolgreich gespeichert!", "success");
            handleSave();
            handleClose();
        } catch (error) {
            console.error(error);
            showFeedback("Es ist ein Fehler aufgetreten!", "error");
        }
    };

    if (!currentContractType) {
        return <>Fehler!</>;
    }
    if (currentContractType.id === 30 && isLoadingPartnerArray) {
        return <LoadingSpinner />;
    }
    return (
        <>
            <Dialog open={isOpenOfferPost} onClose={setIsOpenOfferPost} maxWidth="md" fullWidth>
                <DialogTitle style={{ background: "white", border: "10px solid white" }}>
                    Neue Position(en) hinzufügen
                </DialogTitle>
                <DialogContent>
                    <OfferPositionAdd
                        offerObject={getEmptyOffer(project)}
                        offerPartnerArray={[
                            {
                                id: -1,
                                Offer: -1,
                                Partner: null,
                                selling_price_offer: 80,
                            } as IOfferPartner,
                        ]}
                        partnerArray={[]}
                        offerPositionArray={[]}
                        setOfferPosition={castOfferPosToContractPos}
                        serviceArray={serviceArray}
                        serviceCategoryArray={serviceCategoryArray}
                        setIsOpenAdd={setIsOpenOfferPost}
                    />
                </DialogContent>
            </Dialog>

            <Box sx={{ width: "100%" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <CustomeTextField
                            attr="title"
                            label="Titel"
                            object={editContract}
                            setObject={setEditContract}
                            type="string"
                            disabled={orgContract.ContractState >= 30}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <CustomeTextField
                            attr="contractType"
                            label="Vertragsart"
                            object={currentContractType}
                            setObject={() => {}}
                            type="string"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <CustomeTextField
                            attr="ContractState"
                            label="Vertragsstatus"
                            object={editContract}
                            setObject={setEditContract}
                            type="select"
                            disabled={orgContract.ContractState >= 30}
                        >
                            {contractStateArray.map((state) => (
                                <MenuItem key={`state-${state.id}`} value={state.id}>
                                    {state.contractState}
                                </MenuItem>
                            ))}
                        </CustomeTextField>
                    </Grid>
                    {currentContractType.id === 30 && partnerArray && (
                        <>
                            <Grid item xs={6}>
                                <CustomeTextField
                                    attr="company_name"
                                    label="Partner"
                                    object={
                                        partnerArray.find((x) => x.id === editContract.Partner)?.Contact_Person
                                            ?.Company || {
                                            company_name: "Konnte Partner nicht finden",
                                        }
                                    }
                                    setObject={() => {}}
                                    type="string"
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <CustomeTextField
                                    label="Einkaufspreis pro Stunde"
                                    attr="ppp"
                                    object={pricePerHour}
                                    setObject={recalcContractPositionsPrice}
                                    type="euro"
                                    disabled={editContract.ContractState > 20}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>

                <Collapse in={orgContract.ContractState >= 30} sx={{ mt: 1 }}>
                    <Alert severity="warning">
                        Dieser Vertrag wurde bereits angenommen und kann nichtmehr verändert werden!
                    </Alert>
                </Collapse>

                <Typography sx={{ mt: 5 }} variant="h5">
                    Positionen
                </Typography>
                <Table>
                    <TableHead>
                        {currentContractType.id === 20 ? (
                            <TableRow>
                                <TableCell width={10}>Nr.</TableCell>
                                <TableCell>Titel</TableCell>
                                <TableCell width={150}>Abzahlung in %</TableCell>
                                <TableCell width={150}>Wert</TableCell>
                                <TableCell width={150}>Mwst</TableCell>
                                <TableCell width={10}></TableCell>
                            </TableRow>
                        ) : (
                            <TableRow>
                                <TableCell sx={{ width: 50 }}></TableCell>
                                <TableCell sx={{ width: 80 }}>Artk.Nr.</TableCell>
                                {/* <TableCell sx={{ width: 150}}>Partner</TableCell> */}
                                <TableCell sx={{ minWidth: 300 }}>Position</TableCell>
                                <TableCell sx={{ width: 50 }}></TableCell>
                                <TableCell sx={{ width: 150 }}>Faktor</TableCell>
                                {/*
                                <TableCell sx={{ width: 150}}>P.p.S.</TableCell>
                            */}

                                <TableCell sx={{ width: 150 }}>Einheiten</TableCell>
                                <TableCell sx={{ width: 50 }}>Einheit</TableCell>
                                <TableCell sx={{ width: 150 }}>Preis</TableCell>
                                <TableCell sx={{ width: 50 }}>Total</TableCell>
                                <TableCell sx={{ width: 50 }}></TableCell>
                            </TableRow>
                        )}
                    </TableHead>
                    <TableBody>
                        {contractPositions.map((contractPos, idx) => (
                            <EditorContractPosition
                                key={`contractPosition-${contractPos.id}`}
                                index={idx}
                                contract={orgContract}
                                contractPosition={contractPos}
                                totalValue={totalValue}
                                contractType={currentContractType}
                                setContractPositionArray={setContractPositions}
                                serviceArray={serviceArray}
                                unitArray={unitArray}
                            />
                        ))}
                        {currentContractType.id === 20 ? (
                            <>
                                {/* 1. Nettosumme + Prozentsumme ------------------------------------- */}
                                <TableRow>
                                    {/* leere Spalten: Nr + Titel */}
                                    <TableCell colSpan={2} />

                                    {/* Prozent-Summe */}
                                    <TableCell>
                                        {(contractPositions.reduce((sum, p) => sum + p.units, 0) * 100).toFixed(2)} %
                                    </TableCell>

                                    {/* Nettosumme (editierbar) */}
                                    <TableCell colSpan={2} align="right">
                                        <CustomeTextField
                                            attr="price"
                                            label="Auftragswert (netto)"
                                            object={{ price: totalValue }}
                                            setObject={handleTotalChange}
                                            type="euro"
                                            disabled={isDisabled}
                                            required
                                        />
                                    </TableCell>

                                    {/* freie Spalte für Icon-Column */}
                                    <TableCell />
                                </TableRow>

                                {/* 2. MwSt. --------------------------------------------------------- */}
                                <TableRow>
                                    <TableCell colSpan={3} />
                                    <TableCell align="right" sx={{ borderBottom: 1 }}>
                                        <b>MwSt.</b>
                                    </TableCell>
                                    <TableCell align="right" sx={{ borderBottom: 1 }}>
                                        {(totalValue * 0.19).toLocaleString("de-DE", {
                                            style: "currency",
                                            currency: "EUR",
                                        })}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>

                                {/* 3. Gesamt -------------------------------------------------------- */}
                                <TableRow>
                                    <TableCell colSpan={3} />
                                    <TableCell align="right" sx={{ borderBottom: 1 }}>
                                        <b>Total</b>
                                    </TableCell>
                                    <TableCell align="right" sx={{ borderBottom: 1 }}>
                                        {(totalValue * 1.19).toLocaleString("de-DE", {
                                            style: "currency",
                                            currency: "EUR",
                                        })}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </>
                        ) : (
                            <>
                                <TableRow>
                                    <TableCell colSpan={6} />
                                    <TableCell colSpan={2}>
                                        <b>Summe</b>
                                    </TableCell>
                                    <TableCell>
                                        {totalValue.toLocaleString("de-DE", {
                                            style: "currency",
                                            currency: "EUR",
                                        })}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>

                                <TableRow>
                                    <TableCell colSpan={6} />
                                    <TableCell sx={{ borderBottom: "solid 1px" }} colSpan={2}>
                                        <b>MwSt.</b>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: "solid 1px" }}>
                                        {(totalValue * 0.19).toLocaleString("de-DE", {
                                            style: "currency",
                                            currency: "EUR",
                                        })}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>

                                <TableRow>
                                    <TableCell colSpan={6} />
                                    <TableCell sx={{ borderBottom: "solid 1px" }} colSpan={2}>
                                        <b>Total</b>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: "solid 1px" }}>
                                        {(totalValue * 0.19 + totalValue).toLocaleString("de-DE", {
                                            style: "currency",
                                            currency: "EUR",
                                        })}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>

                <Box
                    sx={{ width: 140, height: 60, float: "right" }}
                    style={{
                        position: "sticky",
                        bottom: 30,
                        right: 0,
                        zIndex: 100,
                        padding: 10,
                        background: "white",
                        border: "10px solid white",
                    }}
                >
                    <Button
                        style={{
                            position: "absolute",
                            bottom: 1,
                            right: 175,
                        }}
                        sx={{ float: "right", width: 150 }}
                        variant="contained"
                        onClick={handleNew}
                        disabled={orgContract.ContractState >= 30}
                    >
                        Neue Position
                    </Button>

                    <Button
                        sx={{
                            position: "absolute",
                            bottom: 1,
                            right: 5,
                            float: "right",
                        }}
                        variant="contained"
                        disabled={isPendingUpdateContract || orgContract.ContractState >= 30}
                        onClick={handleUpload}
                    >
                        {isPendingUpdateContract ? (
                            <Box display="flex" alignItems="center" gap={1}>
                                <span>Speichern</span>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            "Speichern"
                        )}
                    </Button>
                </Box>
            </Box>
        </>
    );
};
